"use client";

import { useCallback, useState } from "react";

import { ModalTemplate } from "@/components/ui/templates/modal-template";
import type { ShareReportReason } from "@/lib/api/api-client";

/**
 * « Signaler un contenu inapproprié. »
 *
 * # Un motif, un texte, et c'est tout
 *
 * Le motif est obligatoire, le texte ne l'est pas. Exiger une explication ferait
 * renoncer une partie de ceux qui signalent, alors que le simple fait qu'un
 * partage soit signalé est déjà l'information ; le texte, quand il est là, est
 * ce qui permet de décider. Trois motifs, pas douze : une liste longue donne
 * l'illusion du tri et produit surtout des choix arbitraires.
 *
 * # Pourquoi la même langue visuelle que le panneau de retour
 *
 * Parce que c'est le même geste : trois choix en bandeau, un texte libre, un
 * envoi. `feedback-panel` l'a déjà réglé au pixel — la coque de modale ne pose
 * aucun rembourrage sur son corps, et son pied répartit ses enfants aux deux
 * bords. Réinventer l'habillage ici, c'était le refaire de travers.
 *
 * L'aide de chaque motif vit dans le **substitut de saisie** plutôt que sous
 * chaque option : trois lignes d'explication permanentes doublent la hauteur de
 * la modale pour un texte qu'on ne lit qu'une fois.
 *
 * # Ce que l'écran promet, et ce qu'il ne promet pas
 *
 * Il dit qu'un humain va regarder. Il ne dit pas que le morceau va disparaître,
 * parce que rien n'est bloqué automatiquement : un bouton qui retirerait tout
 * seul serait une arme, n'importe qui pourrait faire disparaître le morceau de
 * n'importe qui. Promettre un retrait qu'on ne fait pas serait pire que ne rien
 * promettre.
 */

const REASONS: readonly { value: ShareReportReason; label: string; placeholder: string }[] = [
  {
    value: "copyright",
    label: "Droits d'auteur",
    placeholder: "Ce morceau est protégé et ne devrait pas être partagé...",
  },
  {
    value: "illegal",
    label: "Contenu illicite",
    placeholder: "Le fichier ou son titre pose un problème autre que les droits...",
  },
  {
    value: "other",
    label: "Autre",
    placeholder: "Explique en quelques mots...",
  },
];

const MAX_MESSAGE = 2000;

const SUBTLE_FIELD = {
  backgroundColor: "rgba(255,255,255,0.03)",
  border: "1px solid var(--color-border-subtle)",
} as const;

export type ReportDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Ce qu'on signale, pour que l'écran nomme le morceau. */
  readonly pieceTitle: string;
  readonly onSubmit: (reason: ShareReportReason, message: string | null) => Promise<void>;
};

export function ReportDialog({ open, onClose, pieceTitle, onSubmit }: ReportDialogProps) {
  const [reason, setReason] = useState<ShareReportReason | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    if (sending) {
      return;
    }

    setReason(null);
    setMessage("");
    setSent(false);
    setError(null);
    onClose();
  }, [sending, onClose]);

  const submit = useCallback(async () => {
    if (reason === null || sending) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      await onSubmit(reason, message.trim() === "" ? null : message.trim());
      setSent(true);
    } catch {
      setError("Le signalement n'est pas parti. Réessaie dans un instant.");
    } finally {
      setSending(false);
    }
  }, [reason, sending, message, onSubmit]);

  if (sent) {
    return (
      <ModalTemplate
        open={open}
        onClose={close}
        title="Signalement envoyé"
        size="md"
        footer={
          <div className="flex w-full justify-end">
            <button type="button" onClick={close} className={SOLID}>
              Fermer
            </button>
          </div>
        }
      >
        <div className="px-6 py-6">
          <p className="text-[13px] leading-relaxed text-text-secondary">
            Merci. Quelqu&apos;un va le regarder. Le morceau reste accessible en attendant
            cet examen.
          </p>
        </div>
      </ModalTemplate>
    );
  }

  const placeholder =
    REASONS.find((entry) => entry.value === reason)?.placeholder
    ?? "Choisis d'abord un motif.";

  return (
    <ModalTemplate
      open={open}
      onClose={close}
      title="Signaler un contenu inapproprié"
      size="md"
      error={error}
      footer={
        <>
          <button type="button" onClick={close} disabled={sending} className={GHOST}>
            Annuler
          </button>

          <button
            type="button"
            onClick={() => void submit()}
            disabled={reason === null || sending}
            className={SOLID}
          >
            {sending ? "Envoi..." : "Envoyer"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-5 px-6 py-6">
        <p className="text-[13px] leading-relaxed text-text-secondary">
          Tu signales le partage de «&nbsp;
          <span className="text-text-primary">{pieceTitle}</span>&nbsp;».
        </p>

        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-text-muted">
            Motif
          </span>

          <div role="radiogroup" aria-label="Motif du signalement" className="flex gap-2">
            {REASONS.map((entry) => {
              const active = reason === entry.value;

              return (
                <button
                  key={entry.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={sending}
                  onClick={() => setReason(entry.value)}
                  className="flex flex-1 items-center justify-center h-10 rounded-lg px-2 text-[12.5px] font-medium transition-colors duration-150 cursor-pointer disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  style={{
                    color: active
                      ? "var(--color-text-primary)"
                      : "var(--color-text-secondary)",
                    backgroundColor: active
                      ? "var(--color-accent-subtle)"
                      : "rgba(255,255,255,0.03)",
                    border: active
                      ? "1px solid var(--color-accent)"
                      : "1px solid var(--color-border-subtle)",
                  }}
                >
                  {entry.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="report-message"
            className="text-[11px] font-medium uppercase tracking-wider text-text-muted"
          >
            Des précisions, si tu en as
          </label>

          <textarea
            id="report-message"
            value={message}
            onChange={(event) => setMessage(event.target.value.slice(0, MAX_MESSAGE))}
            placeholder={placeholder}
            disabled={sending}
            rows={3}
            className="resize-y rounded-lg px-3 py-2.5 text-[13px] leading-relaxed text-text-primary placeholder:text-text-muted/50 outline-none transition-colors duration-150 disabled:opacity-60"
            style={SUBTLE_FIELD}
            onFocus={(event) => {
              event.currentTarget.style.borderColor = "rgba(139,92,246,0.4)";
            }}
            onBlur={(event) => {
              event.currentTarget.style.borderColor = "var(--color-border-subtle)";
            }}
          />

          <div className="flex justify-end">
            <span className="text-[11px] text-text-muted">
              {message.length}/{MAX_MESSAGE}
            </span>
          </div>
        </div>
      </div>
    </ModalTemplate>
  );
}

const BUTTON =
  "h-9 rounded-lg px-4 text-[12.5px] font-medium transition-all duration-150 cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

const GHOST = `${BUTTON} text-text-secondary hover:text-text-primary hover:bg-white/[0.04] disabled:opacity-50`;

const SOLID = `${BUTTON} font-semibold text-white hover:opacity-90 disabled:opacity-40 bg-accent`;
